import classNames from 'classnames';
import Icon from 'components/Icon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface OverflowTab {
  key: string;
  title: React.ReactNode;
  tabId: string;
}

interface TabListMoreProps {
  label: React.ReactNode;
  tabs: OverflowTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export default function TabListMore({
  label,
  tabs,
  activeTab,
  onTabChange
}: TabListMoreProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0
  });

  const hasActiveChild = tabs.some((t) => t.tabId === activeTab);

  const buttonClasses = classNames('tablist__button tablist__more-button', {
    'tablist__button--active': hasActiveChild
  });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX
      });
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(closeTimeout.current);
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const handleMouseLeave = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpen(false), 200);
  }, []);

  const handleClick = useCallback(() => {
    updatePosition();
    setOpen((prev) => !prev);
  }, [updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div
      className="tablist__more"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a ref={buttonRef} className={buttonClasses} onClick={handleClick}>
        <Icon name="ai-studio" />
        {label}
      </a>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tablist__more-dropdown"
            style={{
              position: 'absolute',
              top: pos.top,
              left: pos.left,
              transform: 'translateX(-100%)'
            }}
            onMouseEnter={() => clearTimeout(closeTimeout.current)}
            onMouseLeave={handleMouseLeave}
          >
            {tabs.map((tab) => (
              <a
                key={tab.key}
                className={classNames('tablist__more-dropdown-item', {
                  'tablist__more-dropdown-item--active':
                    activeTab === tab.tabId
                })}
                onClick={() => {
                  onTabChange(tab.tabId);
                  setOpen(false);
                }}
              >
                {tab.title}
              </a>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
